import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    CheckCircle2,
    AlertTriangle,
    ChevronRight,
    ChevronLeft,
    Save,
    ShieldCheck,
    Home,
    Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'react-hot-toast';
import { complianceApi } from '@/lib/api';
import {
    ComplianceProfile,
    StructuralAttributes,
    LifeSafetyAttributes,
    MechanicalAttributes,
    ConditionStatus
} from '@/types/compliance';

interface M200WizardProps {
    propertyId: string;
    initialData?: Partial<ComplianceProfile>;
}

const STEPS = [
    { id: 'structural', title: 'Structural Envelope', icon: Home },
    { id: 'life-safety', title: 'Life Safety', icon: ShieldCheck },
    { id: 'mechanical', title: 'Mechanical Systems', icon: Zap },
    { id: 'review', title: 'Review & Sign', icon: CheckCircle2 },
];

export const M200Wizard: React.FC<M200WizardProps> = ({ propertyId, initialData }) => {
    const navigate = useNavigate();
    const [currentStepIdx, setCurrentStepIdx] = useState(0);
    const [isSaving, setIsSaving] = useState(false);

    // Form State
    const [structural, setStructural] = useState<StructuralAttributes>(initialData?.structural?.attributes || {
        roofType: 'Shingle',
        windowCount: 0,
        windowsEgressCompliant: false,
        guardrailHeightCompliant: false,
    });

    const [lifeSafety, setLifeSafety] = useState<LifeSafetyAttributes>(initialData?.lifeSafety?.attributes || {
        smokeAlarmType: 'Battery',
        fireExtinguisherLocation: 'None',
        hasEmergencyLighting: false,
    });

    const [mechanical, setMechanical] = useState<MechanicalAttributes>(initialData?.mechanical?.attributes || {
        heatingSource: 'Electric',
        hasDryerDucts: false,
        hasBathroomFans: false,
    });

    // Generic state for condition statuses of elements (simplified for this UI)
    const [elementConditions, setElementConditions] = useState<Record<string, ConditionStatus>>({});

    const currentStep = STEPS[currentStepIdx];

    const handleNext = () => {
        if (currentStepIdx < STEPS.length - 1) {
            setCurrentStepIdx(prev => prev + 1);
        } else {
            handleSubmit();
        }
    };

    const handleBack = () => {
        if (currentStepIdx > 0) {
            setCurrentStepIdx(prev => prev - 1);
        }
    };

    const handleSkip = () => {
        toast('Step skipped. You can complete this later.', { icon: '⚠️' });
        handleNext();
    };

    const handleSubmit = async () => {
        setIsSaving(true);
        try {
            // Construct payload (mapping wizard state to API payload)
            // Note: In a real implementation this would map more comprehensively
            const payload = {
                structural: { attributes: structural },
                lifeSafety: { attributes: lifeSafety },
                mechanical: { attributes: mechanical },
                status: 'Compliant' // Assuming flow completion implies compliance for now, or backend logic determines it
            };

            await complianceApi.updateProfile(propertyId, payload);
            toast.success('Compliance profile updated successfully!');
            navigate('/dashboard/properties/' + propertyId);
        } catch (error) {
            console.error(error);
            toast.error('Failed to save compliance profile.');
        } finally {
            setIsSaving(false);
        }
    };

    const renderConditionSelector = (elementKey: string) => (
        <div className="mt-2">
            <label className="text-xs font-medium text-gray-500">Current Condition</label>
            <div className="mt-1 flex gap-2">
                {(['A', 'B', 'C', 'D'] as const).map((status) => (
                    <button
                        key={status}
                        onClick={() => setElementConditions(prev => ({ ...prev, [elementKey]: status }))}
                        className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold transition-colors ${elementConditions[elementKey] === status
                                ? 'bg-primary-600 text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                        title={
                            status === 'A' ? 'Original / Good' :
                                status === 'B' ? 'Repair < 1 yr' :
                                    status === 'C' ? 'Repair 2-4 yrs' : 'Repair > 5 yrs'
                        }
                    >
                        {status}
                    </button>
                ))}
            </div>
        </div>
    );

    return (
        <div className="mx-auto max-w-4xl rounded-xl bg-white shadow-sm ring-1 ring-gray-200">
            {/* Stepper Header */}
            <div className="border-b px-6 py-4">
                <nav aria-label="Progress">
                    <ol role="list" className="flex items-center">
                        {STEPS.map((step, stepIdx) => (
                            <li key={step.title} className={`${stepIdx !== STEPS.length - 1 ? 'pr-8 sm:pr-20' : ''} relative`}>
                                <div className="flex items-center gap-2">
                                    <div className={`flex h-8 w-8 items-center justify-center rounded-full ${stepIdx <= currentStepIdx ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-400'
                                        }`}>
                                        <step.icon className="h-4 w-4" />
                                    </div>
                                    <span className={`text-sm font-medium ${stepIdx <= currentStepIdx ? 'text-gray-900' : 'text-gray-500'}`}>
                                        {step.title}
                                    </span>
                                </div>
                            </li>
                        ))}
                    </ol>
                </nav>
            </div>

            {/* Content */}
            <div className="p-6">
                <div className="mb-6">
                    <h2 className="text-xl font-bold text-gray-900">{currentStep.title}</h2>
                    <p className="text-sm text-gray-500">Configure the elements related to {currentStep.title.toLowerCase()}.</p>
                </div>

                {currentStep.id === 'structural' && (
                    <div className="space-y-6">
                        <div className="grid gap-6 md:grid-cols-2">
                            <div className="rounded-lg border p-4">
                                <h3 className="font-semibold text-gray-900">Roofing System</h3>
                                <div className="mt-4 space-y-3">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Type</label>
                                        <select
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
                                            value={structural.roofType}
                                            onChange={e => setStructural({ ...structural, roofType: e.target.value as any })}
                                        >
                                            <option value="Shingle">Asphalt Shingle</option>
                                            <option value="Metal">Metal</option>
                                            <option value="Other">Other</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Last Replaced (Year)</label>
                                        <input
                                            type="number"
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
                                            placeholder="YYYY"
                                            value={structural.roofLastReplacedYear || ''}
                                            onChange={e => setStructural({ ...structural, roofLastReplacedYear: parseInt(e.target.value) })}
                                        />
                                    </div>
                                    {renderConditionSelector('roof')}
                                </div>
                            </div>

                            <div className="rounded-lg border p-4">
                                <h3 className="font-semibold text-gray-900">Windows & Doors</h3>
                                <div className="mt-4 space-y-3">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Total Count</label>
                                        <input
                                            type="number"
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
                                            value={structural.windowCount}
                                            onChange={e => setStructural({ ...structural, windowCount: parseInt(e.target.value) })}
                                        />
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            id="egress"
                                            checked={structural.windowsEgressCompliant}
                                            onChange={e => setStructural({ ...structural, windowsEgressCompliant: e.target.checked })}
                                            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                        />
                                        <label htmlFor="egress" className="text-sm text-gray-700">All bedrooms have Egress windows</label>
                                    </div>
                                    {renderConditionSelector('windows')}
                                </div>
                            </div>
                        </div>

                        <div className="rounded-lg bg-orange-50 p-4">
                            <div className="flex">
                                <AlertTriangle className="h-5 w-5 text-orange-400" />
                                <div className="ml-3">
                                    <h3 className="text-sm font-medium text-orange-800">Guardrail Requirement</h3>
                                    <div className="mt-2 text-sm text-orange-700">
                                        <p>Verify that all decks &gt; 24" off the ground have guardrails at least 36" high (42" for new construction).</p>
                                    </div>
                                    <div className="mt-3 flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            id="guardrail"
                                            checked={structural.guardrailHeightCompliant}
                                            onChange={e => setStructural({ ...structural, guardrailHeightCompliant: e.target.checked })}
                                            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                        />
                                        <label htmlFor="guardrail" className="text-sm text-gray-700 font-medium">Confirmed compliant</label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {currentStep.id === 'life-safety' && (
                    <div className="space-y-6">
                        <div className="rounded-lg border p-4">
                            <h3 className="font-semibold text-gray-900">Smoke Alarms</h3>
                            <div className="mt-4 space-y-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">System Type</label>
                                    <select
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
                                        value={lifeSafety.smokeAlarmType}
                                        onChange={e => setLifeSafety({ ...lifeSafety, smokeAlarmType: e.target.value as any })}
                                    >
                                        <option value="Hardwired">Hardwired (Interconnected)</option>
                                        <option value="Battery">Battery Operated</option>
                                        <option value="None">None (Non-Compliant)</option>
                                    </select>
                                </div>
                                {renderConditionSelector('smokeAlarms')}
                                <p className="text-xs text-gray-500">Must be photoelectric and located on every level + in bedrooms.</p>
                            </div>
                        </div>

                        <div className="rounded-lg border p-4">
                            <h3 className="font-semibold text-gray-900">Fire Extinguishers</h3>
                            <div className="mt-4 space-y-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Location</label>
                                    <select
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
                                        value={lifeSafety.fireExtinguisherLocation}
                                        onChange={e => setLifeSafety({ ...lifeSafety, fireExtinguisherLocation: e.target.value as any })}
                                    >
                                        <option value="Unit">In Unit (Kitchen)</option>
                                        <option value="CommonArea">Common Areas</option>
                                        <option value="Both">Both</option>
                                        <option value="None">Missing</option>
                                    </select>
                                </div>
                                {renderConditionSelector('extinguishers')}
                            </div>
                        </div>
                    </div>
                )}

                {currentStep.id === 'mechanical' && (
                    <div className="space-y-6">
                        <div className="rounded-lg border p-4">
                            <h3 className="font-semibold text-gray-900">Heating System</h3>
                            <div className="mt-4 space-y-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Primary Source</label>
                                    <select
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
                                        value={mechanical.heatingSource}
                                        onChange={e => setMechanical({ ...mechanical, heatingSource: e.target.value as any })}
                                    >
                                        <option value="Electric">Electric Baseboard</option>
                                        <option value="Oil">Oil Furnace/Boiler</option>
                                        <option value="Gas">Natural Gas / Propane</option>
                                        <option value="Other">Heat Pump / Other</option>
                                    </select>
                                </div>
                                {mechanical.heatingSource === 'Oil' && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Tank Inspection Date</label>
                                        <input
                                            type="date"
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
                                            value={mechanical.oilTankInspectionDate || ''}
                                            onChange={e => setMechanical({ ...mechanical, oilTankInspectionDate: e.target.value })}
                                        />
                                    </div>
                                )}
                                {renderConditionSelector('heating')}
                            </div>
                        </div>

                        <div className="rounded-lg border p-4">
                            <h3 className="font-semibold text-gray-900">Ventilation</h3>
                            <div className="mt-4 space-y-3">
                                <div className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        id="dryer"
                                        checked={mechanical.hasDryerDucts}
                                        onChange={e => setMechanical({ ...mechanical, hasDryerDucts: e.target.checked })}
                                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                    />
                                    <label htmlFor="dryer" className="text-sm text-gray-700">Dryer ducts vented outside</label>
                                </div>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        id="bath"
                                        checked={mechanical.hasBathroomFans}
                                        onChange={e => setMechanical({ ...mechanical, hasBathroomFans: e.target.checked })}
                                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                    />
                                    <label htmlFor="bath" className="text-sm text-gray-700">Bathroom fans functional</label>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {currentStep.id === 'review' && (
                    <div className="text-center py-10">
                        <ShieldCheck className="mx-auto h-16 w-16 text-green-600" />
                        <h3 className="mt-4 text-xl font-bold text-gray-900">Configuration Complete</h3>
                        <p className="mt-2 text-gray-600">You have defined the mandatory elements for M-200 compliance.</p>
                        <div className="mt-8 rounded-lg bg-gray-50 p-6 text-left max-w-sm mx-auto">
                            <h4 className="font-medium text-gray-900 mb-2">Summary:</h4>
                            <ul className="space-y-2 text-sm text-gray-600">
                                <li>• Structural Envelope: <span className="text-green-600 font-medium">Configured</span></li>
                                <li>• Life Safety: <span className="text-green-600 font-medium">Configured</span></li>
                                <li>• Mechanical: <span className="text-green-600 font-medium">Configured</span></li>
                            </ul>
                        </div>
                    </div>
                )}

            </div>

            {/* Footer Actions */}
            <div className="flex w-full items-center justify-between border-t bg-gray-50 px-6 py-4 rounded-b-xl">
                {currentStepIdx === 0 ? (
                    <div></div> // Spacer
                ) : (
                    <Button variant="outline" onClick={handleBack} disabled={isSaving}>
                        <ChevronLeft className="mr-2 h-4 w-4" />
                        Back
                    </Button>
                )}

                <div className="flex gap-3">
                    {currentStep.id !== 'review' && (
                        <Button variant="ghost" onClick={handleSkip} disabled={isSaving}>
                            Skip for now
                        </Button>
                    )}

                    <Button
                        onClick={handleNext}
                        disabled={isSaving}
                        className={currentStep.id === 'review' ? 'bg-green-600 hover:bg-green-700' : ''}
                    >
                        {isSaving ? 'Saving...' : currentStep.id === 'review' ? 'Finish & Save' : 'Next Step'}
                        {currentStep.id !== 'review' && !isSaving && <ChevronRight className="ml-2 h-4 w-4" />}
                        {currentStep.id === 'review' && !isSaving && <Save className="ml-2 h-4 w-4" />}
                    </Button>
                </div>
            </div>
        </div>
    );
};
